import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { TIP_BOX, placeTip, tipFixedStyle } from "@/lib/tipStyle";

/**
 * Lightweight controlled tooltip. Fixed, in a portal on <body> (no `overflow` or
 * transformed ancestor cuts or shifts it, nor passes it its uppercase/centring), and it
 * always stays on screen: never wider than the screen (minus 8 px each side), below the
 * trigger if it fits, above if it fits there, otherwise on the roomier side with its
 * height capped and a scroll (`lib/tipStyle.placeTip`). Multi-line text via
 * whiteSpace: pre-line — newlines in `text` are rendered.
 *
 * Desktop: shows on mouse hover; when the text needs a scroll, the pointer can move onto
 * the tooltip to scroll it. Touch: tap to open, tap again (or tap elsewhere, or scroll
 * the page) to close — a fixed tooltip must never stay "stuck".
 */
export default function HoverTip({ text, children, maxWidth = 320, dense = false }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: null, scroll: false });
    const wrapRef = useRef(null);
    const tipRef = useRef(null);
    const ptrType = useRef("mouse");
    const hideTimer = useRef(null);

    const cancelHide = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } };
    const show = () => {
        cancelHide();
        if (!wrapRef.current) return;
        const r = wrapRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 8, left: r.left, maxHeight: null, scroll: false });
        setOpen(true);
    };
    const hide = () => { cancelHide(); setOpen(false); };
    // Leaving the trigger: a short grace so the pointer can reach a scrollable tooltip.
    const hideSoon = () => { cancelHide(); hideTimer.current = setTimeout(() => setOpen(false), 150); };
    useEffect(() => cancelHide, []);

    useLayoutEffect(() => {
        if (!open || !wrapRef.current || !tipRef.current) return;
        const tip = tipRef.current;
        // Measure at the left edge: near the right one a fixed box shrinks to the room left.
        tip.style.left = "0px";
        tip.style.maxHeight = "none";
        const next = placeTip(wrapRef.current.getBoundingClientRect(), tip.offsetWidth, tip.scrollHeight);
        // Written here too: if the result equals the state, React won't touch the style again.
        const st = tipFixedStyle(next, maxWidth);
        tip.style.left = `${st.left}px`;
        tip.style.top = `${st.top}px`;
        tip.style.maxHeight = typeof st.maxHeight === "number" ? `${st.maxHeight}px` : st.maxHeight;
        setPos(next);
    }, [open, text]);

    // Once open, dismiss on page scroll/resize or a tap outside so the fixed tooltip never
    // lingers detached from its trigger (scrolling the tooltip itself doesn't count).
    useEffect(() => {
        if (!open) return;
        const inside = (t) => (wrapRef.current && wrapRef.current.contains(t)) || (tipRef.current && tipRef.current.contains(t));
        const onScroll = (e) => { if (!inside(e.target)) hide(); };
        const onResize = () => hide();
        const onDocDown = (e) => { if (!inside(e.target)) hide(); };
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        document.addEventListener("pointerdown", onDocDown, true);
        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            document.removeEventListener("pointerdown", onDocDown, true);
        };
    }, [open]);

    if (!text) return children;
    return (
        <>
            <span
                ref={wrapRef}
                onPointerDown={(e) => { ptrType.current = e.pointerType || "mouse"; }}
                onPointerEnter={(e) => { if ((e.pointerType || "mouse") === "mouse") show(); }}
                onPointerLeave={(e) => { if ((e.pointerType || "mouse") === "mouse") (pos.scroll ? hideSoon() : hide()); }}
                onClick={() => { if (ptrType.current !== "mouse") setOpen((o) => !o); }}
                className="inline-block"
            >
                {children}
            </span>
            {open && createPortal(
                <div
                    ref={tipRef}
                    role="tooltip"
                    onPointerEnter={cancelHide}
                    onClick={(e) => { e.stopPropagation(); if (ptrType.current !== "mouse") hide(); }}
                    onPointerLeave={(e) => { if ((e.pointerType || "mouse") === "mouse") hide(); }}
                    style={{ ...tipFixedStyle(pos, maxWidth), whiteSpace: "pre-line", pointerEvents: pos.scroll ? "auto" : "none" }}
                    className={`${TIP_BOX} text-left normal-case tracking-normal font-normal ${dense ? "text-[10.5px] leading-snug px-2.5 py-1.5" : "text-xs leading-relaxed px-3 py-2"}`}
                    data-testid="hover-tip"
                >
                    {text}
                </div>,
                document.body,
            )}
        </>
    );
}
